import { breakpoints, colors } from '../styles';
import { globalColor } from '../utils/globals';
import ToggleSwitch from './ToggleSwitch';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Container = styled.div``;

// Styled Components
const TableContainer = styled.div`
    height: calc(100vh - 277px);
    overflow: auto;
    position: relative;
    margin-top: 16px; // Add some space between search and table
    table {
        position: relative;
        border-collapse: separate;
        border-spacing: 0 8px; // Control spacing between rows here
    }
    thead {
        position: sticky;
        top: 0;
        z-index: 10;
    }
    @media (max-width: 768px) {
        height: unset;
        overflow: visible;
        margin-bottom: 100px;
    }
`;

const Table = styled.table`
    width: 100%;
    table-layout: fixed;
    font-size: 16px;
    margin: 0;
    ${breakpoints.onlyMobile} {
        font-size: 12px;
    }
`;

// Update Tbody to remove the custom spacing
const Tbody = styled.tbody``;

const Thead = styled.thead`
    background-color: #f0f0f0;
    width: 100%;
`;

const Th = styled.th<{ hideOnMobile?: boolean }>`
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
    background: #f0f0f0;
    border-bottom: 1px solid #d8d8d8;
    border-top: 1px solid #d8d8d8;
    position: sticky;
    top: 0;
    z-index: 10;
    vertical-align: middle;
    &:first-child {
        border-left: 1px solid #d8d8d8;
    }
    &:last-child {
        border-right: 1px solid #d8d8d8;
    }
    ${({ hideOnMobile }) =>
        hideOnMobile &&
        `
    @media (max-width: 768px) {
      display: none;
    }
  `}
    ${breakpoints.onlyMobile} {
        &:nth-child(2) {
            border-right: 1px solid #d8d8d8;
        }
        font-size: 12px;
        padding: 10px 10px;
    }
`;

// Update Tr to handle the row styling
const Tr = styled.tr<{ highlighted?: boolean }>`
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
`;

// Update Td to handle the cell styling
const Td = styled.td<{ hideOnMobile?: boolean; highlighted?: boolean }>`
    padding: 12px 16px;
    border-top: 1px solid
        ${({ highlighted }) =>
            highlighted
                ? globalColor.backgroundColor2 || '#32c77f'
                : '#d8d8d8'};
    border-bottom: 1px solid
        ${({ highlighted }) =>
            highlighted
                ? globalColor.backgroundColor2 || '#32c77f'
                : '#d8d8d8'};
    /* Keep minimal look, but add side borders only */
    &:first-child {
        border-right: none;
        border-left: 1px solid
            ${({ highlighted }) =>
                highlighted
                    ? globalColor.backgroundColor2 || '#32c77f'
                    : '#d8d8d8'};
    }
    &:last-child {
        border-right: 1px solid
            ${({ highlighted }) =>
                highlighted
                    ? globalColor.backgroundColor2 || '#32c77f'
                    : '#d8d8d8'};
        border-left: none;
    }
    ${({ hideOnMobile }) =>
        hideOnMobile &&
        `
    @media (max-width: 768px) {
      display: none;
    }
  `}
    ${breakpoints.onlyMobile} {
        &:nth-child(2) {
            border-right: 1px solid
                ${({ highlighted }) =>
                    highlighted
                        ? globalColor.backgroundColor2 || '#32c77f'
                        : '#d8d8d8'};
        }
        font-size: 12px;
        padding: 10px 10px;
    }
`;

// Update TotalSkillsTd for the numbers column
const TotalSkillsTd = styled(Td)`
    text-align: center;
    cursor: pointer;
`;

const TooltipFloating = styled.div`
    position: fixed;
    background-color: ${colors.black};
    color: ${colors.white};
    padding: 12px;
    border-radius: 4px;
    font-size: 12px;
    max-width: 300px;
    max-height: 80vh;
    overflow-y: auto;
    z-index: 9999;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
    pointer-events: none;
    ${breakpoints.onlyMobile} {
        font-size: 12px;
        max-width: 80%;
    }
`;

const SkillDomainTh = styled(Th)`
    width: 400px;
    ${breakpoints.onlyMobile} {
        width: 80%;
    }
`;

const ToggleTH = styled(Th)`
    width: 80px;
    ${breakpoints.onlyMobile} {
        width: 20%;
    }
`;
const SelectedCounter = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: #222;
    font-style: normal;
    display: flex;
    justify-content: flex-end;
    ${breakpoints.onlyMobile} {
        font-size: 13px;
        margin-right: 2%;
    }
`;

const SearchContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const StyledInput = styled.input`
    padding: 8px 12px;
    font-size: 14px;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 260px;
    &:focus-visible {
        outline: none;
    }
    ${breakpoints.onlyMobile} {
        width: 173px;
        padding: 6px 10px;
        font-size: 12px;
    }
`;

// Data Interfaces
interface Subdomain {
    title: string;
    id: number;
    idx: number;
    explanation?: string;
    level?: any;
}

interface SkillDomain {
    id: number;
    idx: number;
    title: string;
    explanation: string;
    preEvaluationLevel?: any;
    subdomains?: Subdomain[];
}

interface SkillGridProps {
    data: SkillDomain[];
    selectedIds: string[];
    onToggle: (id: string, checked: boolean) => void;
}

// Tooltip helper
const subdomainsList = (
    currentDomain: SkillDomain,
    t: (key: string) => string,
) => {
    let result = '';
    if (Object.hasOwn(currentDomain, 'subdomains')) {
        result += t('skillsIncludedInCluster') + '<br /><br />';
        currentDomain.subdomains?.forEach((subdomain) => {
            result += subdomain?.title + '<br />';
        });
        return result;
    }
    return '';
};

// Grid Component
const OptionalSkillGrid: React.FC<SkillGridProps> = ({
    data,
    selectedIds,
    onToggle,
}) => {
    const { t } = useTranslation(['OptionalSurvey']);
    const [search, setSearch] = useState('');

    // Local state for toggles, synced with selectedIds from parent
    const [selected, setSelected] = useState<Record<number, boolean>>({});
    const [tooltipHtml, setTooltipHtml] = useState('');
    const [tooltipPosition, setTooltipPosition] = useState<{
        x: number;
        y: number;
        transform: string;
    } | null>(null);
    const tooltipRef = React.useRef<HTMLDivElement>(null);
    // Sync local state with selectedIds from parent
    useEffect(() => {
        const initial: Record<number, boolean> = {};
        selectedIds.forEach((id) => {
            initial[Number(id)] = true;
        });
        setSelected(initial);
    }, [selectedIds]);

    const handleToggle = (state: boolean, idStr?: string) => {
        const id = Number(idStr);
        setSelected((prev) => ({ ...prev, [id]: state }));
        onToggle(String(id), state); // Notify parent
    };

    const showTooltip = (event: React.MouseEvent, html: string) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setTooltipHtml(html);

        // Wait for tooltip to render, then measure and adjust position
        setTimeout(() => {
            if (tooltipRef.current) {
                const tooltipHeight = tooltipRef.current.offsetHeight;
                const windowHeight = window.innerHeight;
                const spaceAbove = rect.top;
                const spaceBelow = windowHeight - rect.bottom;

                let top = rect.top;
                const left = rect.left + rect.width / 2;
                let transform = 'translate(-100%, -50%)';

                // If not enough space above, show below
                if (spaceAbove < tooltipHeight && spaceBelow > tooltipHeight) {
                    top = rect.bottom;
                    transform = 'translate(-100%, 0)';
                }
                // If not enough space below, show above
                else if (
                    spaceBelow < tooltipHeight &&
                    spaceAbove > tooltipHeight
                ) {
                    top = rect.top - tooltipHeight;
                    transform = 'translate(-100%, 0)';
                }
                // If neither has enough space, default to below
                else if (
                    spaceBelow < tooltipHeight &&
                    spaceAbove < tooltipHeight
                ) {
                    top = Math.max(0, windowHeight - tooltipHeight - 10);
                    transform = 'translate(-100%, 0)';
                }

                setTooltipPosition({
                    x: left,
                    y: top,
                    transform,
                });
            }
        }, 0);

        // Set initial position so tooltip renders
        setTooltipPosition({
            x: rect.left + rect.width / 2,
            y: rect.top,
            transform: 'translate(-100%, -50%)',
        });
    };

    const hideTooltip = () => {
        setTooltipPosition(null);
    };

    const selectedCount = Object.values(selected).filter(Boolean).length;
    const totalDomains = data.length;

    // Filter data based on search
    const filteredData = search
        ? data.filter((domain) => {
              const searchLower = search.toLowerCase();
              // Check domain title
              if (domain.title.toLowerCase().includes(searchLower)) return true;
              // Check subdomain titles
              if (
                  domain.subdomains &&
                  domain.subdomains.some((sub) =>
                      sub.title.toLowerCase().includes(searchLower),
                  )
              )
                  return true;
              return false;
          })
        : data;

    return (
        <Container>
            {/* Search Input */}
            <SearchContainer>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <StyledInput
                        type="search"
                        placeholder={t('searchPlaceholder') as string}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <SelectedCounter>
                    {selectedCount}/{totalDomains} {t('selected')}
                </SelectedCounter>
            </SearchContainer>
            <TableContainer>
                <Table>
                    <Thead>
                        <tr>
                            <ToggleTH>{t('select')}</ToggleTH>
                            <SkillDomainTh>{t('skillCluster')}</SkillDomainTh>
                            <Th
                                hideOnMobile
                                style={{ width: '120px', textAlign: 'center' }}
                            >
                                {t('totalSkills')}
                            </Th>
                            <Th hideOnMobile>{t('description')}</Th>
                        </tr>
                    </Thead>
                    <Tbody>
                        {filteredData.map((domain) => (
                            <Tr
                                key={domain.id}
                                highlighted={selected[domain.id]}
                            >
                                <Td highlighted={selected[domain.id]}>
                                    <ToggleSwitch
                                        id={String(domain.id)}
                                        isOn={!!selected[domain.id]}
                                        onToggle={handleToggle}
                                    />
                                </Td>
                                <Td highlighted={selected[domain.id]}>
                                    <span>{domain.title}</span>
                                </Td>
                                <TotalSkillsTd
                                    hideOnMobile
                                    highlighted={selected[domain.id]}
                                >
                                    <span
                                        style={{
                                            cursor: 'pointer',
                                            display: 'inline-block',
                                            textDecoration: 'underline',
                                        }}
                                        onMouseEnter={(e) =>
                                            showTooltip(
                                                e,
                                                subdomainsList(domain, t),
                                            )
                                        }
                                        onMouseLeave={hideTooltip}
                                    >
                                        {domain.subdomains?.length ?? 0}
                                    </span>
                                </TotalSkillsTd>
                                <Td
                                    hideOnMobile
                                    highlighted={selected[domain.id]}
                                >
                                    {domain.explanation}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </TableContainer>
            {/* Tooltip */}
            {tooltipPosition && (
                <TooltipFloating
                    ref={tooltipRef}
                    style={{
                        top: tooltipPosition.y,
                        left: tooltipPosition.x,
                        transform: tooltipPosition.transform,
                    }}
                    dangerouslySetInnerHTML={{ __html: tooltipHtml }}
                />
            )}
        </Container>
    );
};

export default OptionalSkillGrid;
